import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLoading } from '../context/LoadingContext';
import {
  ArrowLeftIcon,
  CodeBracketIcon,
  SparklesIcon,
  ClipboardIcon,
  TrashIcon,
  ClockIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import '../styles/scrollbar.css';

// Types
interface GeneratedCode {
  code: string;
  language: string;
  prompt: string;
  timestamp: Date;
}

// Types for language configuration
interface LanguageConfig {
  ext: string;
  mime: string;
  template?: (code: string) => string;
}

type LanguageConfigMap = {
  [key: string]: LanguageConfig;
};

// Language configurations with file extensions and MIME types
const LANGUAGE_CONFIG: LanguageConfigMap = {
  javascript: { ext: '.js', mime: 'text/javascript' },
  typescript: { ext: '.ts', mime: 'text/typescript' },
  python: { ext: '.py', mime: 'text/x-python' },
  java: { ext: '.java', mime: 'text/x-java-source' },
  csharp: { ext: '.cs', mime: 'text/x-csharp' },
  cpp: { ext: '.cpp', mime: 'text/x-c++src' },
  php: { ext: '.php', mime: 'application/x-httpd-php' },
  ruby: { ext: '.rb', mime: 'text/x-ruby' },
  swift: { ext: '.swift', mime: 'text/x-swift' },
  go: { ext: '.go', mime: 'text/x-go' },
  rust: { ext: '.rs', mime: 'text/x-rustsrc' },
  html: { 
    ext: '.html', 
    mime: 'text/html',
    template: (code: string) => {
      if (!code.includes('<!DOCTYPE html>')) {
        return `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Generated HTML</title>
    <style>
        /* Add your styles here */
    </style>
</head>
<body>
    ${code}
</body>
</html>`;
      }
      return code;
    }
  },
  css: { 
    ext: '.css', 
    mime: 'text/css',
    template: (code: string) => {
      if (!code.includes('/*')) {
        return `/*
 * Generated CSS Styles
 * Created: ${new Date().toLocaleString()}
 */

${code}

/* Media Queries */
@media screen and (max-width: 768px) {
    /* Add responsive styles here */
}`;
      }
      return code;
    }
  },
  sql: { ext: '.sql', mime: 'text/x-sql' },
  xml: { ext: '.xml', mime: 'application/xml' },
  json: { ext: '.json', mime: 'application/json' },
  yaml: { ext: '.yml', mime: 'text/yaml' },
  markdown: { ext: '.md', mime: 'text/markdown' },
  shell: { ext: '.sh', mime: 'text/x-shellscript' },
  powershell: { ext: '.ps1', mime: 'text/x-powershell' },
  dockerfile: { ext: 'Dockerfile', mime: 'text/x-dockerfile' },
};

const handleDownload = (code: string, language: string) => {
  try {
    const config = LANGUAGE_CONFIG[language as keyof typeof LANGUAGE_CONFIG] || { ext: '.txt', mime: 'text/plain' };
    const timestamp = new Date().getTime();
    const fileName = `nexon-generated_${language}_${timestamp}${config.ext}`;
    
    // Apply template if available
    let processedCode = code;
    if (config.template) {
      processedCode = config.template(code);
    }
    
    const blob = new Blob([processedCode], { type: config.mime });
    const element = document.createElement('a');
    element.href = URL.createObjectURL(blob);
    element.download = fileName;
    
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
    URL.revokeObjectURL(element.href);
  } catch (error) {
    console.error('Error downloading file:', error);
  }
};

// Components
const Header: React.FC<{
  showHistory: boolean;
  onToggleHistory: () => void;
}> = ({ showHistory, onToggleHistory }) => (
  <div className="fixed top-0 left-0 right-0 z-50 bg-gray-900/80 backdrop-blur-lg border-b border-gray-700">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex items-center justify-between h-16">
        <div className="flex items-center space-x-4">
          <Link
            to="/generation"
            className="inline-flex items-center text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </Link>
          <div className="flex items-center space-x-3">
            <div className="h-3 w-3 bg-green-500 rounded-full animate-pulse" />
            <h1 className="text-xl font-bold text-white">Code Generation</h1>
          </div>
        </div>

        <button
          onClick={onToggleHistory}
          className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
            showHistory
              ? 'bg-gray-700 text-white'
              : 'text-gray-400 hover:text-white'
          }`}
          title={showHistory ? 'Hide History' : 'Show History'}
        >
          <ClockIcon className="w-5 h-5" />
          <span className="text-sm font-medium">History</span>
        </button>
      </div>
    </div>
  </div>
);

const CodePromptForm: React.FC<{
  prompt: string;
  language: string;
  loading: boolean;
  onPromptChange: (value: string) => void;
  onLanguageChange: (value: string) => void;
  onSubmit: (e: React.FormEvent) => void;
}> = ({ prompt, language, loading, onPromptChange, onLanguageChange, onSubmit }) => (
  <form onSubmit={onSubmit} className="mb-8">
    <div className="flex flex-col space-y-4">
      <div className="flex gap-4">
        <div className="flex-1">
          <textarea
            value={prompt}
            onChange={(e) => onPromptChange(e.target.value)}
            placeholder="Describe the code you want to generate..."
            className="w-full h-24 px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none text-white placeholder-gray-400"
            disabled={loading}
          />
        </div>
        <div className="w-48">
          <select
            value={language}
            onChange={(e) => onLanguageChange(e.target.value)}
            className="w-full h-24 px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-white"
            disabled={loading}
          >
            <option value="">Select Language</option>
            <option value="javascript">JavaScript</option>
            <option value="typescript">TypeScript</option>
            <option value="python">Python</option>
            <option value="java">Java</option>
            <option value="cpp">C++</option>
            <option value="csharp">C#</option>
            <option value="php">PHP</option>
            <option value="ruby">Ruby</option>
            <option value="swift">Swift</option>
            <option value="go">Go</option>
            <option value="rust">Rust</option>
            <option value="html">HTML</option>
            <option value="css">CSS</option>
            <option value="sql">SQL</option>
            <option value="xml">XML</option>
            <option value="json">JSON</option>
            <option value="yaml">YAML</option>
            <option value="markdown">Markdown</option>
            <option value="shell">Shell Script</option>
            <option value="powershell">PowerShell</option>
            <option value="dockerfile">Dockerfile</option>
          </select>
        </div>
      </div>
      <div className="flex justify-end">
        <button
          type="submit"
          disabled={loading || !prompt.trim() || !language}
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-200 ${
            loading || !prompt.trim() || !language
              ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
              : 'bg-gradient-to-r from-green-600 to-emerald-600 text-white hover:from-green-700 hover:to-emerald-700 shadow-lg hover:shadow-green-500/25'
          }`}
        >
          {loading ? (
            <>
              <SparklesIcon className="w-5 h-5 animate-spin" />
              <span>Generating...</span>
            </>
          ) : (
            <>
              <CodeBracketIcon className="w-5 h-5" />
              <span>Generate</span>
            </>
          )}
        </button>
      </div>
    </div>
  </form>
);

const CodeCard: React.FC<{
  item: GeneratedCode;
  onDelete: () => void;
  onCopy: () => void;
}> = ({ item, onDelete, onCopy }) => (
  <div className="bg-gray-800 rounded-lg overflow-hidden border border-gray-700 transition-transform hover:scale-[1.02] hover:shadow-xl">
    <div className="p-4 border-b border-gray-700 flex items-center justify-between">
      <div>
        <span className="text-sm font-medium text-gray-300">{item.language}</span>
        <p className="text-xs text-gray-500 mt-1">{formatDate(item.timestamp)}</p>
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={() => handleDownload(item.code, item.language)}
          className="p-2 bg-green-500/20 hover:bg-green-500/40 rounded-lg text-green-400 hover:text-green-300 transition-colors"
          title="Download file"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
              d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
        </button>
        <button
          onClick={onCopy}
          className="p-2 bg-blue-500/20 hover:bg-blue-500/40 rounded-lg text-blue-400 hover:text-blue-300 transition-colors"
          title="Copy code"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
              d="M15.666 3.888A2.25 2.25 0 0 0 13.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 0 1-.75.75H9a.75.75 0 0 1-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 0 1-2.25 2.25H6.75A2.25 2.25 0 0 1 4.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 0 1 1.927-.184" />
          </svg>
        </button>
        <button
          onClick={onDelete}
          className="p-2 bg-red-500/20 hover:bg-red-500/40 rounded-lg text-red-400 hover:text-red-300 transition-colors"
          title="Delete code"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
              d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
          </svg>
        </button>
      </div>
    </div>
    <div className="p-4">
      <p className="text-sm text-gray-300 mb-4">{item.prompt}</p>
      <pre className="bg-gray-900 p-4 rounded-lg overflow-x-auto">
        <code className={`language-${item.language} text-sm`}>{item.code}</code>
      </pre>
    </div>
  </div>
);

const HistorySidebar: React.FC<{
  show: boolean;
  items: GeneratedCode[];
  onClose: () => void;
  onDelete: (index: number) => void;
  onCopy: (code: string) => void;
}> = ({ show, items, onClose, onDelete, onCopy }) => (
  <div
    className={`fixed right-0 top-0 bottom-0 w-80 bg-gray-800 border-l border-gray-700 transform transition-transform duration-300 ease-in-out ${
      show ? 'translate-x-0' : 'translate-x-full'
    } z-50`}
  >
    <div className="h-full flex flex-col">
      <div className="p-4 border-b border-gray-700">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <ClockIcon className="w-5 h-5 text-green-400" />
            History
          </h2>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-400">{items.length} items</span>
            <button
              onClick={onClose}
              className="p-1 text-gray-400 hover:text-white transition-colors"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {items.length > 0 ? (
          items.map((item, index) => (
            <div
              key={index}
              className="group relative bg-gray-700/50 rounded-lg p-3 hover:bg-gray-700/70 transition-colors"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-300">
                  {item.language}
                </span>
                <span className="text-xs text-gray-400">
                  {formatDate(item.timestamp)}
                </span>
              </div>
              <p className="text-sm text-gray-300 line-clamp-2 mb-2">
                {item.prompt}
              </p>
              <pre className="bg-gray-800 p-2 rounded text-xs overflow-x-auto mb-2">
                <code className={`language-${item.language}`}>
                  {item.code.length > 100
                    ? `${item.code.slice(0, 100)}...`
                    : item.code}
                </code>
              </pre>
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => onCopy(item.code)}
                  className="p-1.5 rounded-full text-gray-400 hover:text-blue-400 transition-colors"
                  title="Copy code"
                >
                  <ClipboardIcon className="w-4 h-4" />
                </button>
                <button
                  onClick={() => onDelete(index)}
                  className="p-1.5 rounded-full text-gray-400 hover:text-red-400 transition-colors"
                  title="Delete code"
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-8">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gray-700/50 mb-3">
              <ClockIcon className="w-6 h-6 text-gray-400" />
            </div>
            <p className="text-gray-400 text-sm">No history yet</p>
            <p className="text-gray-500 text-xs mt-1">
              Generated code will appear here
            </p>
          </div>
        )}
      </div>
    </div>
  </div>
);

const EmptyState: React.FC = () => (
  <div className="text-center py-12">
    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-800 mb-4">
      <CodeBracketIcon className="w-8 h-8 text-gray-400" />
    </div>
    <h3 className="text-lg font-medium text-gray-300 mb-2">No code generated yet</h3>
    <p className="text-gray-500">Start by describing the code you want to create</p>
  </div>
);

// Utility Functions
const formatDate = (date: Date) => {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);
};

// Utility function to clean and format code blocks
const formatCodeBlock = (code: string): string => {
  return code
    .replace(/^\n+|\n+$/g, '') // Remove extra newlines at start/end
    .replace(/\t/g, '  ')      // Convert tabs to spaces
    .replace(/\n\s*\n\s*\n/g, '\n\n') // Remove extra blank lines
    .trim();
};

const extractCodeBlock = (text: string, lang: string): string => {
  // Remove any BOM or special characters
  text = text.replace(/^\ufeff/g, '');
  
  // Try to find code block with language marker
  const langPattern = `${lang}|${lang.toLowerCase()}|${lang.toUpperCase()}`;
  const codeBlockRegex = new RegExp(
    "```(?:" + langPattern + ")?\\n?([\\s\\S]*?)```"
  );
  const match = text.match(codeBlockRegex);
  
  if (match && match[1]) {
    return formatCodeBlock(match[1]);
  }
  
  // Try to find any code block
  const anyCodeBlockRegex = /```(?:\w+)?\n?([\s\S]*?)```/;
  const anyMatch = text.match(anyCodeBlockRegex);
  
  if (anyMatch && anyMatch[1]) {
    return formatCodeBlock(anyMatch[1]);
  }
  
  // If no code block found, try to clean up the text
  return formatCodeBlock(text);
};

// Main Component
const CodeGeneration: React.FC = () => {
  const [prompt, setPrompt] = useState('');
  const [language, setLanguage] = useState('');
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<GeneratedCode[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const { setLoading: setPageLoading } = useLoading();

  useEffect(() => {
    const initializePage = async () => {
      setPageLoading(true);
      try {
        const savedItems = localStorage.getItem('generatedCode');
        if (savedItems) {
          const parsedItems = JSON.parse(savedItems).map((item: any) => ({
            ...item,
            timestamp: new Date(item.timestamp)
          }));
          setItems(parsedItems);
        }
        await new Promise(resolve => setTimeout(resolve, 1000));
      } catch (error) {
        console.error('Error loading saved code:', error);
      } finally {
        setPageLoading(false);
      }
    };

    initializePage();
  }, [setPageLoading]);

  useEffect(() => {
    localStorage.setItem('generatedCode', JSON.stringify(items));
  }, [items]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!prompt.trim() || !language || loading) return;

    setLoading(true);

    try {
      const response = await fetch(
        `https://api.nexon.my.id/api/chat-gpt?q=${encodeURIComponent(prompt.trim() + ' language ' + language + ' code only')}&apikey=${process.env.REACT_APP_NEXON_API_KEY}`
      );

      if (!response.ok) {
        throw new Error('Failed to generate code');
      }

      const data = await response.json();
      if (data && data.status) {
        const codeBlock = extractCodeBlock(data.data, language);
        
        const newItem: GeneratedCode = {
          code: codeBlock,
          language,
          prompt: prompt.trim(),
          timestamp: new Date()
        };
        setItems(prev => [newItem, ...prev]);
        setPrompt('');
        setLanguage('');
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
    } catch (error) {
      console.error('Error copying to clipboard:', error);
    }
  };

  const handleDelete = (index: number) => {
    setItems(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <Header
        showHistory={showHistory}
        onToggleHistory={() => setShowHistory(!showHistory)}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24">
        {/* Page Title and Description */}
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-white mb-4">AI Code Generation</h2>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto">
            Generate code snippets in various programming languages using our advanced AI model.
            Simply describe what you want to create, and let AI handle the implementation.
          </p>
        </div>

        {/* Code Generation Form */}
        <div className="max-w-4xl mx-auto mb-12">
          <CodePromptForm
            prompt={prompt}
            language={language}
            loading={loading}
            onPromptChange={setPrompt}
            onLanguageChange={setLanguage}
            onSubmit={handleSubmit}
          />
        </div>

        {/* Generated Code Grid */}
        <div className="space-y-6 pb-8">
          {items.map((item, index) => (
            <CodeCard
              key={index}
              item={item}
              onDelete={() => handleDelete(index)}
              onCopy={() => handleCopy(item.code)}
            />
          ))}
        </div>

        {items.length === 0 && !loading && <EmptyState />}
      </div>

      <HistorySidebar
        show={showHistory}
        items={items}
        onClose={() => setShowHistory(false)}
        onDelete={handleDelete}
        onCopy={handleCopy}
      />
    </div>
  );
};

export default CodeGeneration;
